import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, Plus, ChevronRight, Play } from 'lucide-react';
import { api } from '../services/api';
import { useAuthStore } from '../store/auth';

export function DashboardPage() {
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();

  const { data: inventories, isLoading } = useQuery({
    queryKey: ['inventories'],
    queryFn: () => api.inventories.list(),
  });

  // Calculate total items and find the most recently updated inventory
  const { totalItems, lastInventoryId } = useMemo(() => {
    if (!inventories || inventories.length === 0) {
      return { totalItems: 0, lastInventoryId: null };
    }
    const total = inventories.reduce((sum, inv) => sum + (inv.itemCount || 0), 0);
    // Inventories are sorted by updatedAt desc, so first one is most recent
    const lastId = inventories[0]?.id || null;
    return { totalItems: total, lastInventoryId: lastId };
  }, [inventories]);

  const hasItems = totalItems > 0;

  const createInventoryMutation = useMutation({
    mutationFn: (name: string) => api.inventories.create({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventories'] });
    },
  });

  const handleNewInventory = () => {
    const name = prompt('Enter inventory name:', 'My Inventory');
    if (name && name.trim()) {
      createInventoryMutation.mutate(name.trim());
    }
  };

  return (
    <div className="space-y-8">
      {/* Welcome section */}
      <div className="card p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.displayName}!
        </h1>
        <p className="mt-2 text-gray-600">
          Ready to inventory your home? Just talk naturally and we'll catalog everything.
        </p>

        <Link
          to={hasItems && lastInventoryId ? `/add-inventory?inventoryId=${lastInventoryId}` : '/add-inventory'}
          className="mt-6 inline-flex items-center gap-3 btn-primary text-lg px-6 py-3"
        >
          {hasItems ? (
            <>
              <Play className="w-5 h-5" />
              Resume Inventory
            </>
          ) : (
            <>
              <Plus className="w-5 h-5" />
              Start Inventory
            </>
          )}
        </Link>
      </div>

      {/* Inventories */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Your Inventories</h2>
          <button
            onClick={handleNewInventory}
            disabled={createInventoryMutation.isPending}
            className="btn-secondary text-sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            {createInventoryMutation.isPending ? 'Creating...' : 'New Inventory'}
          </button>
        </div>

        {isLoading ? (
          <div className="card p-8 text-center text-gray-500">Loading...</div>
        ) : inventories?.length === 0 ? (
          <div className="card p-8 text-center text-gray-500">
            No inventories yet. Start a voice session to create one!
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {inventories?.map((inventory) => (
              <Link
                key={inventory.id}
                to={`/inventory/${inventory.id}`}
                className="card p-4 hover:border-primary-300 transition-colors group relative"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <Package className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 group-hover:text-primary-600">
                        {inventory.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {inventory.itemCount} items
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600" />
                </div>
                {inventory.description && (
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                    {inventory.description}
                  </p>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
